import React from 'react'
import { Box, TextField, Autocomplete, Stack, FormControlLabel, Switch, InputAdornment, Chip } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { SearchFormTemplateDto } from '@/core/models/form.model'
import { t } from '@lingui/macro'
import { useLanguage } from '@/hooks/useLanguage'
import { getCategoryLabel, getTagLabel } from '../vocabularyLabels'

interface FormTemplateSearchProps {
  value: SearchFormTemplateDto
  onChange: (value: SearchFormTemplateDto) => void
  availableCategories: string[]
  availableTags: string[]
}

function FormTemplateSearch({ value, onChange, availableCategories, availableTags }: FormTemplateSearchProps) {
  const lang = useLanguage()

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...value,
      searchText: event.target.value,
    })
  }

  const handleCategoriesChange = (_: any, newValue: string[]) => {
    onChange({
      ...value,
      categories: newValue.length > 0 ? newValue : undefined,
    })
  }

  const handleTagsChange = (_: any, newValue: string[]) => {
    onChange({
      ...value,
      tags: newValue.length > 0 ? newValue : undefined,
    })
  }

  const handleActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...value,
      isActive: event.target.checked,
    })
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Stack spacing={2}>
        <TextField
          fullWidth
          placeholder={t`Search form templates...`}
          value={value.searchText || ''}
          onChange={handleSearchTextChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <Stack direction="row" spacing={2}>
          <Autocomplete
            multiple
            options={availableCategories}
            value={value.categories || []}
            onChange={handleCategoriesChange}
            getOptionLabel={(option) => getCategoryLabel(option, lang)}
            renderInput={(params) => (
              <TextField {...params} label={t`Categories`} placeholder={t`Select categories`} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option} label={getCategoryLabel(option, lang)} color="primary" variant="outlined" />
              ))
            }
            sx={{ flex: 1 }}
            freeSolo
            filterSelectedOptions
          />

          <Autocomplete
            multiple
            options={availableTags}
            value={value.tags || []}
            onChange={handleTagsChange}
            getOptionLabel={(option) => getTagLabel(option, lang)}
            renderInput={(params) => (
              <TextField {...params} label={t`Tags`} placeholder={t`Select tags`} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option} label={getTagLabel(option, lang)} color="secondary" variant="outlined" />
              ))
            }
            sx={{ flex: 1 }}
            freeSolo
            filterSelectedOptions
          />
        </Stack>

        <div>
          <FormControlLabel
            control={<Switch checked={value.isActive ?? true} onChange={handleActiveChange} color="primary" />}
            label={t`Show only active templates`}
          />
        </div>
      </Stack>
    </Box>
  )
}

export default FormTemplateSearch
